/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { HeadingTagType } from "@lexical/rich-text";
import type { NodeKey } from "lexical";

import "./index.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useRef, useState } from "react";
import * as React from "react";

// Custom Table of Contents Hook
function useTableOfContents() {
  const [editor] = useLexicalComposerContext();
  const [tableOfContents, setTableOfContents] = useState<
    Array<[key: NodeKey, text: string, tag: HeadingTagType]>
  >([]);

  useEffect(() => {
    const updateTableOfContents = () => {
      editor.getEditorState().read(() => {
        const root = editor.getRootElement();
        if (!root) return;

        const headings = root.querySelectorAll("h1, h2, h3, h4, h5, h6");
        const toc: Array<[key: NodeKey, text: string, tag: HeadingTagType]> =
          [];

        headings.forEach((heading) => {
          const node = editor.getEditorState().read(() => {
            const element = heading as HTMLElement;
            return element.getAttribute("data-lexical-key");
          });

          if (node) {
            const text = heading.textContent || "";
            const tag = heading.tagName.toLowerCase() as HeadingTagType;
            toc.push([node, text, tag]);
          }
        });

        setTableOfContents(toc);
      });
    };

    const removeUpdateListener = editor.registerUpdateListener(() => {
      updateTableOfContents();
    });

    updateTableOfContents();
    return removeUpdateListener;
  }, [editor]);

  return tableOfContents;
}

function indent(tagName: HeadingTagType) {
  if (tagName === "h2") {
    return "heading2";
  } else if (tagName === "h3") {
    return "heading3";
  }
  return "heading1";
}

function TableOfContentsList({
  tableOfContents,
}: {
  tableOfContents: Array<[key: NodeKey, text: string, tag: HeadingTagType]>;
}): React.JSX.Element {
  const [selectedKey, setSelectedKey] = useState("");
  const selectedIndex = useRef(0);
  const [editor] = useLexicalComposerContext();

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.getEditorState().read(() => {
      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.scrollIntoView();
        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }
  function isHeadingAtTheTopOfThePage(element: HTMLElement): boolean {
    const elementYPosition = element?.getClientRects()[0].y;
    return elementYPosition > 0.26 && elementYPosition < 9;
  }
  function isHeadingAboveViewport(element: HTMLElement): boolean {
    const elementYPosition = element?.getClientRects()[0].y;
    return elementYPosition <= 0;
  }
  function isHeadingBelowTheTopOfThePage(element: HTMLElement): boolean {
    const elementYPosition = element?.getClientRects()[0].y;
    return elementYPosition > 9;
  }

  useEffect(() => {
    function scrollCallback() {
      if (
        tableOfContents.length !== 0 &&
        selectedIndex.current < tableOfContents.length - 1
      ) {
        let currentHeading = editor.getElementByKey(
          tableOfContents[selectedIndex.current][0]
        );
        if (currentHeading !== null) {
          if (isHeadingBelowTheTopOfThePage(currentHeading)) {
            //On natural scroll, user is scrolling up
            while (
              currentHeading !== null &&
              isHeadingBelowTheTopOfThePage(currentHeading) &&
              selectedIndex.current > 0
            ) {
              const prevHeading = editor.getElementByKey(
                tableOfContents[selectedIndex.current - 1][0]
              );
              if (
                prevHeading !== null &&
                (isHeadingAboveViewport(prevHeading) ||
                  isHeadingBelowTheTopOfThePage(prevHeading))
              ) {
                selectedIndex.current--;
              }
              currentHeading = prevHeading;
            }
            const prevHeadingKey = tableOfContents[selectedIndex.current][0];
            setSelectedKey(prevHeadingKey);
          } else if (isHeadingAboveViewport(currentHeading)) {
            //On natural scroll, user is scrolling down
            while (
              currentHeading !== null &&
              isHeadingAboveViewport(currentHeading) &&
              selectedIndex.current < tableOfContents.length - 1
            ) {
              const nextHeading = editor.getElementByKey(
                tableOfContents[selectedIndex.current + 1][0]
              );
              if (
                nextHeading !== null &&
                (isHeadingAtTheTopOfThePage(nextHeading) ||
                  isHeadingAboveViewport(nextHeading))
              ) {
                selectedIndex.current++;
              }
              currentHeading = nextHeading;
            }
            const nextHeadingKey = tableOfContents[selectedIndex.current][0];
            setSelectedKey(nextHeadingKey);
          }
        }
      } else {
        selectedIndex.current = 0;
      }
    }
    let timerId: ReturnType<typeof setTimeout>;

    function debounceFunction(func: () => void, delay: number) {
      clearTimeout(timerId);
      timerId = setTimeout(func, delay);
    }

    function onScroll(): void {
      debounceFunction(scrollCallback, 10);
    }

    document.addEventListener("scroll", onScroll);
    return () => document.removeEventListener("scroll", onScroll);
  }, [tableOfContents, editor]);

  return (
    <ul className="remove-ul-style">
      {tableOfContents.map(([key, text, tag], index) => (
        <div
          className={selectedKey === key ? "selectedHeading" : "heading"}
          key={key}
          onClick={() => scrollToNode(key, index)}
          role="button"
          tabIndex={0}
        >
          <div className={selectedKey === key ? "circle" : "bar"} />
          <li className={indent(tag)}>
            {("" + text).length > 27 ? text.substring(0, 27) + "..." : text}
          </li>
        </div>
      ))}
    </ul>
  );
}

export default function TableOfContentsPlugin(): React.JSX.Element {
  const tableOfContents = useTableOfContents();

  return <TableOfContentsList tableOfContents={tableOfContents} />;
}
